import { Component } from '@theme/component';

class GlobalMartCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.stop();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-gm-track]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    this.index = 0;
    this.reasons = new Set();
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.pauseButton = this.querySelector('[data-gm-pause]');
    this.manualPause = false;
    this.querySelector('[data-gm-controls]')?.removeAttribute('hidden');
    this.querySelector('[data-gm-prev]')?.addEventListener('click', () => this.move(-1, true), { signal });
    this.querySelector('[data-gm-next]')?.addEventListener('click', () => this.move(1, true), { signal });
    this.pauseButton?.addEventListener('click', () => {
      this.manualPause = !this.manualPause;
      this.updatePauseLabel();
    }, { signal });
    this.addEventListener('pointerenter', e => {
      if (e.pointerType === 'mouse') this.reasons.add('hover');
    }, { signal });
    this.addEventListener('pointerleave', () => this.reasons.delete('hover'), { signal });
    this.addEventListener('focusin', () => this.reasons.add('focus'), { signal });
    this.addEventListener('focusout', e => {
      if (!this.contains(e.relatedTarget)) this.reasons.delete('focus');
    }, { signal });
    this.track.addEventListener('pointerdown', () => this.interact(), { signal, passive: true });
    this.track.addEventListener('wheel', () => this.interact(), { signal, passive: true });
    this.track.addEventListener('keydown', e => {
      if (e.target !== this.track) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const rtl = getComputedStyle(this).direction === 'rtl';
        this.move((e.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1), true);
      }
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.reasons.add('hidden') : this.reasons.delete('hidden');
    }, { signal });
    if (document.hidden) this.reasons.add('hidden');
    this.reasons.add('offscreen');
    this.observer = new IntersectionObserver(entries => {
      entries[0].isIntersecting ? this.reasons.delete('offscreen') : this.reasons.add('offscreen');
    }, { threshold: 0.25 });
    this.observer.observe(this);
    document.addEventListener('shopify:block:select', e => {
      const slide = this.slides.findIndex(el => el.contains(e.target) || el === e.target);
      if (slide >= 0) {
        this.reasons.add('editor');
        this.show(slide);
      }
    }, { signal });
    document.addEventListener('shopify:block:deselect', () => this.reasons.delete('editor'), { signal });
    this.reduced.addEventListener('change', () => this.updatePauseLabel(), { signal });
    this.updatePauseLabel();
    this.timer = setInterval(() => {
      if (this.dataset.autoplay === 'true' && !this.manualPause && !this.reduced.matches && !this.reasons.size) this.move(1, false);
    }, Number(this.dataset.interval) || 4500);
  }

  updatePauseLabel() {
    if (!this.pauseButton) return;
    const paused = this.manualPause || this.reduced.matches;
    this.pauseButton.textContent = paused ? this.pauseButton.dataset.play : this.pauseButton.dataset.pause;
    this.pauseButton.setAttribute('aria-pressed', String(paused));
    this.pauseButton.disabled = this.reduced.matches;
  }

  interact() {
    this.reasons.add('interaction');
    clearTimeout(this.idle);
    this.idle = setTimeout(() => this.reasons.delete('interaction'), 8000);
  }

  move(step, manual) {
    if (this.slides.length < 2) return;
    if (manual) this.interact();
    if (this.dataset.mode === 'products') {
      if (this.track.scrollWidth <= this.track.clientWidth + 2) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const r = this.track.getBoundingClientRect();
      const current = this.slides.reduce((best, el, i) => {
        const box = el.getBoundingClientRect();
        const distance = Math.abs(rtl ? box.right - r.right : box.left - r.left);
        return distance < best.distance ? { distance, i } : best;
      }, { distance: Infinity, i: 0 });
      this.index = current.i;
      const atEnd = Math.abs(this.track.scrollLeft) >= this.track.scrollWidth - this.track.clientWidth - 2;
      if (step > 0 && atEnd) this.index = -1;
    }
    this.show((this.index + step + this.slides.length) % this.slides.length);
  }

  show(index) {
    this.index = index;
    if (this.dataset.mode === 'hero') {
      this.slides.forEach((el, i) => {
        el.hidden = i !== index;
        el.classList.toggle('gm-enter', i === index);
      });
      const counter = this.querySelector('[data-gm-counter]');
      if (counter) counter.textContent = `${index + 1} / ${this.slides.length}`;
    } else {
      const rtl = getComputedStyle(this).direction === 'rtl';
      const box = this.slides[index].getBoundingClientRect();
      const track = this.track.getBoundingClientRect();
      const offset = rtl ? box.right - track.right : box.left - track.left;
      this.track.scrollTo({ left: this.track.scrollLeft + offset, behavior: this.reduced.matches ? 'instant' : 'smooth' });
    }
  }

  stop() {
    this.controller?.abort();
    this.observer?.disconnect();
    clearInterval(this.timer);
    clearTimeout(this.idle);
  }

  disconnectedCallback() {
    this.stop();
    super.disconnectedCallback();
  }
}

if (!customElements.get('gm-carousel')) customElements.define('gm-carousel', GlobalMartCarousel);
